// PTPR - Private Tree Plantation Registration
// This Certificate shows the ownership of plantations or planted trees within private, titled lands or tax declared alienable and
// disposable lands. The issuance of PTPR requires inventory and ocular inspection in the area. Tree inventory for permits (e.g. TCP or PLTP)
// is a process conducted separately from the inspection for PTPR per existing DENR policies, rules and regulations.

import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import '@/components/ui/styles/customScrollBar.css';
import { cn } from "@/lib/utils";
import { formatLabel, UploadCard } from '@/pages/user/ApplicationForms/CSAWForm/CSAWFormUtils';
import { gql, useMutation } from '@apollo/client';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import FormStepIndicator from '../FormStepIndicator';

const CREATE_PTPR_PERMIT = gql`
   mutation CreatePTPRPermit($input: PTPRPermitInput!) {
      createPTPRPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         files {
            letterRequest { filename contentType }
            titleOrTaxDeclaration { filename contentType }
            darCertification { filename contentType }
            specialPowerOfAttorney { filename contentType }
         }
      }
   }
`;

const SAVE_PTPR_PERMIT_DRAFT = gql`
   mutation SavePTPRPermitDraft($input: PTPRPermitInput!) {
      savePTPRPermitDraft(input: $input) {
         id
         applicationNumber
         status
         files {
            letterRequest { filename contentType }
            titleOrTaxDeclaration { filename contentType }
            darCertification { filename contentType }
            specialPowerOfAttorney { filename contentType }
         }
      }
   }
`;

const PTPRForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('ptprFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('ptprFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Private Tree Plantation Registration',
         ownerName: '',
         address: '',
         contactNumber: '',
         lotArea: '',
         treeSpecies: Array.isArray(savedFormData?.treeSpecies) ? savedFormData.treeSpecies : [''],
         totalTrees: '',
         treeSpacing: '',
         yearPlanted: '',
         files: {
            letterRequest: [],
            titleOrTaxDeclaration: [],
            darCertification: [],
            specialPowerOfAttorney: []
         },
         dateOfSubmission: '',
         status: '',
         isCLOA: false,
         isRepresentative: false,
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [createPTPRPermit] = useMutation(CREATE_PTPR_PERMIT);
   const [savePTPRPermitDraft] = useMutation(SAVE_PTPR_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [validationErrors, setValidationErrors] = useState({});
   const firstInvalidFieldRef = useRef(null);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));

      // Clear validation error for this field
      if (validationErrors[name]) {
         setValidationErrors(prev => ({
            ...prev,
            [name]: undefined
         }));
      }
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [...prev.files[documentType], ...newFiles]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const handleNextStep = () => {
      const errors = validateStep(currentStep);
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
         // Use setTimeout to ensure DOM is updated
         setTimeout(() => {
            const firstErrorField = document.querySelector('[data-error="true"]');
            if (firstErrorField) {
               firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
               firstErrorField.focus();
            }
         }, 100);
         return;
      }

      setCurrentStep(prev => prev + 1);
   };

   const handlePrevStep = () => {
      setCurrentStep(prev => prev - 1);
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            ownerName: formData.ownerName,
            address: formData.address,
            contactNumber: formData.contactNumber,
            lotArea: parseFloat(formData.lotArea),
            treeSpecies: formData.treeSpecies,
            totalTrees: parseInt(formData.totalTrees, 10),
            treeSpacing: formData.treeSpacing,
            yearPlanted: parseInt(formData.yearPlanted, 10),
            isCLOA: Boolean(formData.isCLOA),
            isRepresentative: Boolean(formData.isRepresentative),
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files && files.length > 0) {
               input.files[key] = await Promise.all(files.map(async file => ({
                  filename: file.name,
                  contentType: file.type,
                  data: await readFileAsBase64(file)
               })));
            } else {
               input.files[key] = []; // Use an empty array if no files
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await savePTPRPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.savePTPRPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);

            localStorage.removeItem('ptprFormStep');
            localStorage.removeItem('ptprFormData');
         }
      } catch (error) {
         console.error('Error saving draft:', error);
         toast.error("Error saving draft: " + error.message);
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();
      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            ownerName: formData.ownerName,
            address: formData.address,
            contactNumber: formData.contactNumber,
            lotArea: parseFloat(formData.lotArea),
            treeSpecies: formData.treeSpecies,
            totalTrees: parseInt(formData.totalTrees, 10),
            treeSpacing: formData.treeSpacing,
            yearPlanted: parseInt(formData.yearPlanted, 10),
            isCLOA: Boolean(formData.isCLOA),
            isRepresentative: Boolean(formData.isRepresentative),
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         console.log('Submitting input:', input);

         const token = localStorage.getItem('token');
         const { data } = await createPTPRPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.createPTPRPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your application?'
            });
            setModalOpen(true);

            localStorage.removeItem('ptprFormStep');
            localStorage.removeItem('ptprFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         toast.error("Error submitting application: " + error.message);
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Helper function to read file as base64
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = error => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const steps = [
      { title: "Owner Details", description: "Fill in owner information" },
      { title: "Plantation Details", description: "Provide plantation information" },
      { title: "Document Requirements", description: "Answer questions for document requirements" },
      { title: "Upload Documents", description: "Upload necessary documents" },
      { title: "Review Your Application", description: "Review and submit your application" },
   ];

   useEffect(() => {
      localStorage.setItem('ptprFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('ptprFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('ptprFormStep');
            localStorage.removeItem('ptprFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Private Tree Plantation Registration',
               ownerName: '',
               address: '',
               contactNumber: '',
               lotArea: '',
               treeSpecies: [''],
               totalTrees: '',
               treeSpacing: '',
               yearPlanted: '',
               files: {
                  letterRequest: [],
                  titleOrTaxDeclaration: [],
                  darCertification: [],
                  specialPowerOfAttorney: []
               },
               dateOfSubmission: '',
               status: '',
               isCLOA: false,
               isRepresentative: false,
            });
            // Reset current step
            setCurrentStep(0);
         }
      };
   }, []);

   const handleCheckboxChange = (e) => {
      const { name, checked } = e.target;

      // Map of checkbox names to their corresponding file types
      const fileTypeMap = {
         isCLOA: 'darCertification',
         isRepresentative: 'specialPowerOfAttorney'
      };

      // Clear associated files if requirement is unchecked
      if (!checked && fileTypeMap[name]) {
         setFormData(prev => ({
            ...prev,
            [name]: checked,
            files: {
               ...prev.files,
               [fileTypeMap[name]]: [] // Clear the files for this requirement
            }
         }));
      } else {
         setFormData(prev => ({ ...prev, [name]: checked }));
      }
   };

   const validateStep = (stepNumber) => {
      // Check if validation is disabled in DevSettings
      const isValidationEnabled = localStorage.getItem('ptprValidationEnabled') !== 'false';
      if (!isValidationEnabled) {
         return {}; // Return empty errors object if validation is disabled
      }

      const errors = {};

      if (stepNumber === 0) {
         if (!formData.ownerName?.trim()) {
            errors.ownerName = 'Owner name is required';
         }
         if (!formData.address?.trim()) {
            errors.address = 'Address is required';
         }
         if (!formData.contactNumber?.trim()) {
            errors.contactNumber = 'Contact number is required';
         }
      }

      if (stepNumber === 1) {
         if (!formData.lotArea) {
            errors.lotArea = 'Lot area is required';
         }
         if (!formData.treeSpacing?.trim()) {
            errors.treeSpacing = 'Tree spacing is required';
         }
         if (formData.treeSpecies.length === 0 || formData.treeSpecies.some(s => s.trim() === '')) {
            errors.treeSpecies = 'At least one tree species is required';
         }
         if (!formData.totalTrees) {
            errors.totalTrees = 'Total number of trees is required';
         }
         if (!formData.yearPlanted) {
            errors.yearPlanted = 'Year planted is required';
         }
      }

      if (stepNumber === 3) {
         // Validate required files
         if (!formData.files.letterRequest?.length) {
            errors.letterRequest = 'Letter request is required';
         }
         if (!formData.files.titleOrTaxDeclaration?.length) {
            errors.titleOrTaxDeclaration = 'Title or tax declaration is required';
         }
         if (formData.isCLOA && !formData.files.darCertification?.length) {
            errors.darCertification = 'DAR certification is required for CLOA holders';
         }
         if (formData.isRepresentative && !formData.files.specialPowerOfAttorney?.length) {
            errors.specialPowerOfAttorney = 'Special power of attorney is required for representatives';
         }
      }

      return errors;
   };

   // Update the render of input fields to include validation
   const renderInput = (id, label, type = "text", placeholder = "") => (
      <div className="space-y-2">
         <Label htmlFor={id}>{label}</Label>
         <div
            className={cn(
               validationErrors[id] && "border-2 border-red-500 rounded-md p-1"
            )}
            data-error={validationErrors[id] ? "true" : "false"}
         >
            <Input
               id={id}
               name={id}
               type={type}
               value={formData[id] || ''}
               onChange={handleInputChange}
               placeholder={placeholder}
               required
               className="bg-background"
            />
            {validationErrors[id] && (
               <div className="flex items-center mt-1 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors[id]}
               </div>
            )}
         </div>
      </div>
   );

   // Update the file upload cards to include validation
   const renderUploadCard = (documentType, label, description) => (
      <div
         className={cn(
            validationErrors[documentType] && "border-2 border-red-500 rounded-md p-1"
         )}
         data-error={validationErrors[documentType] ? "true" : "false"}
      >
         <UploadCard
            label={label}
            documentLabel={description}
            files={formData.files[documentType]}
            onFileChange={(e) => {
               handleFileChange(e, documentType);
               if (validationErrors[documentType]) {
                  setValidationErrors(prev => ({ ...prev, [documentType]: undefined }));
               }
            }}
            onRemoveFile={(file) => removeFile(documentType, file)}
         />
         {validationErrors[documentType] && (
            <div className="flex items-center mt-1 text-sm text-red-500">
               <AlertCircle className="h-4 w-4 mr-1" />
               {validationErrors[documentType]}
            </div>
         )}
      </div>
   );

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-4 flex-grow">
            <h1 className="text-3xl font-[700] text-green-800 dark:text-green-500 mb-6 text-center">
               Private Tree Plantation Registration
            </h1>
            <div className="max-w-2xl mx-auto mb-6">
               <FormStepIndicator currentStep={currentStep + 1} formType="PTPR" />
            </div>
            <Card className="max-w-2xl mx-auto shadow-lg bg-background">
               <CardHeader>
                  <CardTitle className="text-foreground">{steps[currentStep].title}</CardTitle>
               </CardHeader>
               <CardContent>
                  <form onSubmit={handleSubmit}>
                     {currentStep === 0 && (
                        <div className="space-y-4">
                           {renderInput('ownerName', 'Name of Lot Owner', 'text', 'Enter full name')}
                           {renderInput('address', 'Address', 'text', 'Enter complete address')}
                           {renderInput('contactNumber', 'Contact Number', 'text', 'Enter contact number')}
                        </div>
                     )}

                     {currentStep === 1 && (
                        <div className="space-y-4">
                           {renderInput('lotArea', 'Lot area devoted to plantation (in hectares)', 'number', '0.2374')}
                           {renderInput('treeSpacing', 'Spacing of trees', 'text', '2-meter spacing')}
                           <div className="space-y-2">
                              <Label>Tree Species Planted</Label>
                              <div className="space-y-3">
                                 {formData.treeSpecies.map((species, index) => (
                                    <div key={index} className="flex gap-2 items-center">
                                       <Input
                                          value={species}
                                          onChange={(e) => {
                                             const newSpecies = [...formData.treeSpecies];
                                             newSpecies[index] = e.target.value;
                                             setFormData(prev => ({ ...prev, treeSpecies: newSpecies }));
                                          }}
                                          placeholder={`Species ${index + 1}`}
                                          className="flex-1"
                                       />
                                       <Button
                                          type="button"
                                          variant="destructive"
                                          size="sm"
                                          onClick={() => {
                                             if (formData.treeSpecies.length > 1) {
                                                const newSpecies = formData.treeSpecies.filter((_, i) => i !== index);
                                                setFormData(prev => ({ ...prev, treeSpecies: newSpecies }));
                                             }
                                          }}
                                          disabled={formData.treeSpecies.length === 1}
                                       >
                                          Remove
                                       </Button>
                                    </div>
                                 ))}
                                 <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setFormData(prev => ({
                                       ...prev,
                                       treeSpecies: [...prev.treeSpecies, '']
                                    }))}
                                 >
                                    Add Another Species
                                 </Button>
                              </div>
                              {validationErrors.treeSpecies && (
                                 <div className="flex items-center mt-1 text-sm text-red-500">
                                    <AlertCircle className="h-4 w-4 mr-1" />
                                    {validationErrors.treeSpecies}
                                 </div>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                 Click "Add Another Species" for each distinct tree species
                              </p>
                           </div>
                           {renderInput('totalTrees', 'Total No. of Trees Planted', 'number')}
                           {renderInput('yearPlanted', 'Year Planted', 'number')}
                        </div>
                     )}

                     {currentStep === 2 && (
                        <div className="space-y-4">
                           <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 font-semibold">
                              Please answer the following questions to determine required documents.
                           </p>
                           <div className="space-y-4">
                              <div className="flex items-center space-x-2">
                                 <Checkbox
                                    id="isCLOA"
                                    checked={formData.isCLOA}
                                    onCheckedChange={(checked) =>
                                       handleCheckboxChange({ target: { name: 'isCLOA', checked } })
                                    }
                                 />
                                 <Label htmlFor="isCLOA">
                                    Is your Title a Certificate of Land Ownership Award (CLOA)?
                                 </Label>
                              </div>

                              <div className="flex items-center space-x-2">
                                 <Checkbox
                                    id="isRepresentative"
                                    checked={formData.isRepresentative}
                                    onCheckedChange={(checked) =>
                                       handleCheckboxChange({ target: { name: 'isRepresentative', checked } })
                                    }
                                 />
                                 <Label htmlFor="isRepresentative">
                                    Are you a representative of the lot owner?
                                 </Label>
                              </div>
                           </div>
                        </div>
                     )}

                     {currentStep === 3 && (
                        <div className="space-y-4">
                           <div className="h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                              {renderUploadCard(
                                 'letterRequest',
                                 'Letter Request',
                                 'Upload Letter Request (1 original, 1 photocopy)'
                              )}
                              {renderUploadCard(
                                 'titleOrTaxDeclaration',
                                 'Title or Tax Declaration',
                                 'Upload Title or Tax Declaration'
                              )}
                              {formData.isCLOA && renderUploadCard(
                                 'darCertification',
                                 'DAR Certification',
                                 'Upload DAR Certification'
                              )}
                              {formData.isRepresentative && renderUploadCard(
                                 'specialPowerOfAttorney',
                                 'Special Power of Attorney',
                                 'Upload Special Power of Attorney'
                              )}
                           </div>
                        </div>
                     )}

                     {currentStep === 4 && (
                        <div className="space-y-6 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                           <div className="review-step-container custom-scrollbar flex-grow overflow-auto">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Application Type
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.applicationType}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Owner Name
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.ownerName}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Address
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.address}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Contact Number
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.contactNumber}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Lot Area
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.lotArea} hectares
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Tree Species
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {Array.isArray(formData.treeSpecies) ? formData.treeSpecies.join(', ') : 'No species specified'}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Total Trees
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.totalTrees}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Tree Spacing
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.treeSpacing}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Year Planted
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.yearPlanted}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Is CLOA
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.isCLOA ? 'Yes' : 'No'}
                                    </p>
                                 </div>
                                 <div className="bg-background border rounded-lg p-3">
                                    <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                       Is Representative
                                    </h4>
                                    <p className="text-foreground text-sm">
                                       {formData.isRepresentative ? 'Yes' : 'No'}
                                    </p>
                                 </div>
                              </div>

                              <div className="bg-background border rounded-lg p-3 mt-4">
                                 <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                                    Uploaded Files
                                 </h4>
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {Object.entries(formData.files).map(([docType, files]) => {
                                       if (docType === 'darCertification' && !formData.isCLOA) return null;
                                       if (docType === 'specialPowerOfAttorney' && !formData.isRepresentative) return null;

                                       return (
                                          <div key={docType} className="border-b dark:border-gray-700 pb-2">
                                             <h5 className="font-medium text-foreground mb-1 text-xs">
                                                {formatLabel(docType)}
                                             </h5>
                                             {files.length > 0 ? (
                                                <ul className="list-disc list-inside">
                                                   {files.map((file, index) => (
                                                      <li key={index} className="text-xs text-muted-foreground">
                                                         {file.name}
                                                      </li>
                                                   ))}
                                                </ul>
                                             ) : (
                                                <p className="text-xs text-muted-foreground">No files uploaded</p>
                                             )}
                                          </div>
                                       );
                                    })}
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}
                  </form>
               </CardContent>
               <CardFooter className="mt-14 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="w-full sm:w-auto">
                     {currentStep > 0 && (
                        <Button
                           type="button"
                           variant="outline"
                           onClick={handlePrevStep}
                           className="w-full sm:w-auto"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Previous
                        </Button>
                     )}
                  </div>
                  <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
                     {currentStep < steps.length - 1 ? (
                        <Button
                           type="button"
                           onClick={handleNextStep}
                           className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Next
                        </Button>
                     ) : (
                        <>
                           <Button
                              type="button"
                              variant="outline"
                              onClick={handleSaveAsDraft}
                              className="w-full sm:w-auto"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSavingDraft ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                 </>
                              ) : (
                                 'Save as Draft'
                              )}
                           </Button>
                           <Button
                              type="submit"
                              onClick={handleSubmit}
                              className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSubmitting ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                 </>
                              ) : (
                                 'Submit Application'
                              )}
                           </Button>
                        </>
                     )}
                  </div>
               </CardFooter>
            </Card>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
};

export default PTPRForm;
